import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {useQuery} from '@apollo/react-hooks';

import {EntityPill} from 'shells/entity-pill/entity-pill';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';
import {MarketingTitle} from 'shells/typography/marketing-title';

import type {
    GetGlobalBlocklistedEmails as GetGlobalBlocklistedEmailsQuery,
    GetGlobalBlocklistedEmails_globallyBlocklistedEmails as BlocklistedEmail,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import GET_GLOBAL_BLOCKLISTED_EMAILS from '../graphql/queries/get-global-blocklisted-emails.graphql';

import styles from './global-email-blocklist.module.css';

type Props = {
    blocklistedEmails: BlocklistedEmail[];
};

function GlobalEmailBlocklistComponent(props: Props) {
    const [isCollapsed, setIsCollapsed] = React.useState(true);
    const {user} = useGetSessionUser();
    const allBlocklistedEmails = props.blocklistedEmails.map((value) => value.emailAddress);

    const displayedBlocklistedEmails = isCollapsed
        ? allBlocklistedEmails.slice(0, 3)
        : allBlocklistedEmails;

    const renderCollapseButton = () => {
        if (allBlocklistedEmails.length > 3) {
            return (
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className={styles['collapse-button']}
                >
                    {isCollapsed
                        ? `+${allBlocklistedEmails.length - displayedBlocklistedEmails.length} more`
                        : 'Collapse blocklist'}
                </button>
            );
        }
    };

    if (!props.blocklistedEmails.length) {
        return (
            <React.Fragment>
                <MarketingTitle>Addresses blocked for your entire company</MarketingTitle>
                <Body as='i'>No blocklisted email addresses</Body>
                {user && user.permissions.canAccessSetup ? (
                    <Link
                        variant='primary'
                        size='small'
                        to='/setup/email-security'
                        // @ts-expect-error
                        as={RoutedLink}
                        onClick={() => {
                            window.scrollTo(0, 0);
                        }}
                        className={styles['manage-button']}
                    >
                        Manage blocklist
                    </Link>
                ) : undefined}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <MarketingTitle>Addresses blocked for your entire company</MarketingTitle>
            <div className={styles['global-blocklist-container']}>
                {displayedBlocklistedEmails.map((value) => (
                    <EntityPill
                        key={value}
                        name={value}
                        type='light'
                        isLinkable={false}
                        isSmall={true}
                    />
                ))}
                {renderCollapseButton()}
                {user && user.permissions.canAccessSetup ? (
                    <Link
                        variant='primary'
                        size='small'
                        to='/setup/email-security'
                        // @ts-expect-error
                        as={RoutedLink}
                        onClick={() => {
                            window.scrollTo(0, 0);
                        }}
                        className={styles['manage-button']}
                    >
                        Manage blocklist
                    </Link>
                ) : undefined}
            </div>
        </React.Fragment>
    );
}

export function GlobalEmailBlocklist() {
    const {data} = useQuery<GetGlobalBlocklistedEmailsQuery, Record<any, any>>(
        GET_GLOBAL_BLOCKLISTED_EMAILS,
        {
            fetchPolicy: 'cache-and-network',
        }
    );

    const globallyBlocklistedEmails = data ? data.globallyBlocklistedEmails : null;

    if (!globallyBlocklistedEmails) return null;

    return <GlobalEmailBlocklistComponent blocklistedEmails={globallyBlocklistedEmails} />;
}
